import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useCanEdit } from "@/api/session";
import {
  Badge,
  Button,
  ButtonLink,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Quantity,
  Select,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  type DataTableColumn,
  type SortState,
} from "@/components";
import { Decimal } from "@/lib/decimal";
import { formatDate, formatFractionAsPercent, pluralize } from "@/lib/format";
import { useSearchParamsInPlace } from "@/lib/hooks";
import {
  useLocations,
  useLotFacets,
  useLots,
  useLowStock,
  useOrders,
  type LotFilters,
  type Lot,
  type LotOrigin,
  type LowMaterial,
} from "../api";
import { LocationSelect } from "../components/LocationSelect";
import { LotBadges } from "../components/LotBadges";
import { AdjustLotDialog, DisposeLotDialog, MoveLotDialog, ReorderPointDialog } from "../components/LotDialogs";
import { OrdersTable } from "../components/OrdersTable";
import { PrintLabelsDialog } from "../components/PrintLabelsDialog";
import { ReceiveLotDialog, RemoveFromOrderDialog } from "../components/ReceiveDialogs";
import { StockMeter } from "../components/StockMeter";
import { dilutionText, lotPath, receiveOrderPath } from "../lotDisplay";
import styles from "./InventoryPage.module.css";

const PAGE_SIZE = 100;
const SORT_KEYS: Record<string, string> = {
  code: "code",
  material: "material",
  location: "location",
  left: "balance",
  status: "status",
  received: "received",
};
const STATUS_OPTIONS = [
  { value: "default", label: "In stock and empty" },
  { value: "in_stock", label: "In stock" },
  { value: "empty", label: "Empty" },
  { value: "on_order", label: "On order" },
  { value: "disposed", label: "Disposed" },
  { value: "all", label: "All, on order and disposed included" },
];
type LotAction = "move" | "adjust" | "dispose" | "label" | "receive" | "remove";
type InventoryTab = "all" | "orders" | "low";

function inactiveRowClass(lot: Lot): string | undefined {
  return lot.status === "active" || lot.status === "on_order" ? undefined : styles.inactiveRow;
}

function tabFromParam(param: string | null): InventoryTab {
  return param === "low" || param === "orders" ? param : "all";
}

function sortFromParam(param: string | null): SortState {
  const value = param ?? "material";
  const direction = value.startsWith("-") ? "desc" : "asc";
  const key = value.replace(/^-/, "");
  const columnId = Object.entries(SORT_KEYS).find(([, sortKey]) => sortKey === key)?.[0] ?? "material";
  return { columnId, direction };
}

/** Every lot with what's left in it, what's on order, and what runs low; filters, sorting and the
 * tab live in the URL. */
export function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const tab = tabFromParam(searchParams.get("tab"));
  const page = Math.max(0, Number.parseInt(searchParams.get("page") ?? "0", 10) || 0);
  const [searchText, setSearchText] = useState(searchParams.get("q") ?? "");
  const searchTimer = useRef<number | undefined>(undefined);
  const sort = sortFromParam(searchParams.get("sort"));

  useEffect(() => () => window.clearTimeout(searchTimer.current), []);

  function update(changes: Record<string, string | null>, { keepPage = false } = {}) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        if (!keepPage) next.delete("page");
        return next;
      },
      { preventScrollReset: !("page" in changes) },
    );
  }

  function changeSearch(text: string) {
    setSearchText(text);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => update({ q: text.trim() || null }), 250);
  }

  function clearFilters() {
    window.clearTimeout(searchTimer.current);
    setSearchText("");
    setSearchParams(tab === "all" ? {} : { tab }, { replace: true });
  }

  const statusParam = searchParams.get("status");
  const filters: LotFilters = {
    q: searchParams.get("q") || undefined,
    status: (statusParam && statusParam !== "default" ? statusParam : undefined) as LotFilters["status"],
    location: searchParams.get("location") || undefined,
    origin: (searchParams.get("origin") || undefined) as LotOrigin | undefined,
    supplier: searchParams.get("supplier") || undefined,
    material: searchParams.get("material") || undefined,
    import_job: searchParams.get("import") || undefined,
    sort: searchParams.get("sort") || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  const lots = useLots(filters);
  const facets = useLotFacets();
  const lowStock = useLowStock(tab === "low");
  const orders = useOrders("all", tab === "orders");
  const locations = useLocations();
  const [action, setAction] = useState<{ kind: LotAction; lot: Lot } | null>(null);
  const [reorderFor, setReorderFor] = useState<LowMaterial | null>(null);

  const filtersActive = Boolean(
    filters.q ||
    filters.status ||
    filters.location ||
    filters.origin ||
    filters.supplier ||
    filters.material ||
    filters.import_job,
  );
  const materialName =
    filters.material && lots.data?.items.find((lot) => lot.material.id === filters.material)?.material.name;

  const columns: DataTableColumn<Lot>[] = [
    {
      id: "code",
      header: "Code",
      mono: true,
      width: "fit",
      sortable: true,
      cell: (lot) => (
        <Link to={lotPath(lot.code)} className={styles.codeLink}>
          {lot.display_code}
        </Link>
      ),
    },
    {
      id: "material",
      header: "Material",
      width: "wide",
      sortable: true,
      cell: (lot) => (
        <span className={styles.materialCell}>
          <span className={styles.materialName}>{lot.material.name}</span>
          <span className={styles.secondary}>
            {dilutionText(lot)}
            {lot.supplier_name ? `, ${lot.supplier_name}` : ""}
          </span>
        </span>
      ),
    },
    {
      id: "location",
      header: "Location",
      width: "fit",
      sortable: true,
      cell: (lot) =>
        lot.location ? (
          <Tooltip content={lot.location.path}>
            <Link to={`/inventory/locations?focus=${lot.location.id}`} className={styles.locationLink}>
              {lot.location.code}
            </Link>
          </Tooltip>
        ) : (
          <span className={styles.secondary}>—</span>
        ),
    },
    {
      id: "left",
      header: "Left",
      sortable: true,
      cell: (lot) => (
        <span className={styles.meterCell}>
          <StockMeter lot={lot} />
        </span>
      ),
    },
    { id: "status", header: "Status", width: "fit", sortable: true, cell: (lot) => <LotBadges lot={lot} /> },
    {
      id: "received",
      header: "Received",
      width: "fit",
      sortable: true,
      cell: (lot) => <span className={styles.date}>{formatDate(lot.received_at)}</span>,
    },
    {
      id: "opened",
      header: "Opened",
      width: "fit",
      cell: (lot) => <span className={styles.date}>{formatDate(lot.opened_at)}</span>,
    },
  ];

  const lowMaterialColumns: DataTableColumn<LowMaterial>[] = [
    {
      id: "material",
      header: "Material",
      width: "wide",
      cell: (item) => (
        <Link to={`/inventory?material=${item.material.id}`} className={styles.codeLink}>
          {item.material.name}
        </Link>
      ),
    },
    {
      id: "left",
      header: "Left in stock",
      numeric: true,
      cell: (item) => <Quantity value={item.balance_g} unit="g" />,
    },
    { id: "min", header: "Reorder below", numeric: true, cell: (item) => <Quantity value={item.min_g} unit="g" /> },
    { id: "lots", header: "Active lots", numeric: true, cell: (item) => item.lot_count },
    {
      id: "on_order",
      header: "On order",
      numeric: true,
      cell: (item) =>
        Decimal.tryFrom(item.on_order_g)?.isZero() === false ? (
          <Quantity value={item.on_order_g} unit="g" />
        ) : (
          <span className={styles.secondary}>—</span>
        ),
    },
  ];

  const onOrderLotActions = (lot: Lot) => [
    { label: "Open lot", icon: "externalLink" as const, onSelect: () => void navigate(lotPath(lot.code)) },
    ...(canEdit
      ? [
          {
            label: "Receive…",
            icon: "check" as const,
            onSelect: () => {
              if (lot.supplier_order) void navigate(receiveOrderPath(lot.supplier_order.id, lot.id));
              else setAction({ kind: "receive", lot });
            },
          },
        ]
      : []),
    { label: "Print label…", icon: "tag" as const, onSelect: () => setAction({ kind: "label", lot }) },
    ...(canEdit
      ? [
          {
            label: "Remove from order…",
            icon: "x" as const,
            danger: true,
            onSelect: () => setAction({ kind: "remove", lot }),
          },
        ]
      : []),
  ];

  const shelfLotActions = (lot: Lot) => [
    { label: "Open lot", icon: "externalLink" as const, onSelect: () => void navigate(lotPath(lot.code)) },
    ...(canEdit && lot.status !== "disposed"
      ? [
          { label: "Move…", icon: "box" as const, onSelect: () => setAction({ kind: "move", lot }) },
          { label: "Adjust stock…", icon: "pencil" as const, onSelect: () => setAction({ kind: "adjust", lot }) },
        ]
      : []),
    { label: "Print label…", icon: "tag" as const, onSelect: () => setAction({ kind: "label", lot }) },
    ...(canEdit && lot.status !== "disposed"
      ? [
          {
            label: "Dispose…",
            icon: "trash" as const,
            danger: true,
            onSelect: () => setAction({ kind: "dispose", lot }),
          },
        ]
      : []),
  ];

  const lotActions = (lot: Lot) => (lot.status === "on_order" ? onOrderLotActions(lot) : shelfLotActions(lot));

  const total = lots.data?.count ?? 0;
  const firstShown = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Inventory"
        description="Every container on your shelves and what's left in it."
        meta={
          facets.data ? (
            <>
              <Badge>{pluralize(facets.data.total, "lot", "lots")}</Badge>
              {facets.data.on_order ? (
                <Badge tone="info" icon="truck">
                  {facets.data.on_order} on order
                </Badge>
              ) : null}
              {facets.data.low_stock ? (
                <Badge tone="warning" icon="alertTriangle">
                  {facets.data.low_stock} low
                </Badge>
              ) : null}
            </>
          ) : null
        }
        actions={
          <>
            <ButtonLink to="/inventory/locations" variant="ghost" icon="box">
              Locations
            </ButtonLink>
            <ButtonLink to="/inventory/stocktake" icon="scale">
              Stocktake
            </ButtonLink>
            <ButtonLink to="/imports?source=order_paste" icon="upload">
              Paste an order
            </ButtonLink>
            {canEdit ? (
              <ButtonLink to="/inventory/receive" variant="primary" icon="plus">
                Receive stock
              </ButtonLink>
            ) : null}
          </>
        }
      />

      <Tabs.Root value={tab} onValueChange={(next) => update({ tab: next === "all" ? null : next })}>
        <Tabs.List label="Inventory views">
          <Tabs.Tab value="all">All lots</Tabs.Tab>
          <Tabs.Tab value="orders">On order{facets.data?.on_order ? ` (${facets.data.on_order})` : ""}</Tabs.Tab>
          <Tabs.Tab value="low">Low stock{facets.data?.low_stock ? ` (${facets.data.low_stock})` : ""}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all" className={styles.panel}>
          <Toolbar
            label="Filter lots"
            end={
              filtersActive ? (
                <Button variant="ghost" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null
            }
          >
            <TextField
              label="Search lots"
              hideLabel
              type="search"
              size="sm"
              prefix={<Icon name="search" />}
              placeholder="Code, material, synonym or supplier lot"
              value={searchText}
              onValueChange={changeSearch}
              spellCheck={false}
              className={styles.search}
            />
            <Select
              label="Status"
              size="sm"
              options={STATUS_OPTIONS}
              value={statusParam ?? "default"}
              onValueChange={(value) => update({ status: value === "default" ? null : value })}
            />
            {locations.data?.length ? (
              <LocationSelect
                label="Location"
                size="sm"
                value={filters.location ?? null}
                onValueChange={(value) => update({ location: value })}
                noneLabel="All locations"
              />
            ) : null}
            <Select
              label="Origin"
              size="sm"
              options={[
                { value: "any", label: "Received and made" },
                { value: "received", label: "Received" },
                { value: "produced", label: "Made by a batch" },
              ]}
              value={filters.origin ?? "any"}
              onValueChange={(value) => update({ origin: value === "any" ? null : value })}
            />
            {facets.data && facets.data.suppliers.length > 0 ? (
              <Select
                label="Supplier"
                size="sm"
                options={[
                  { value: "any", label: "All suppliers" },
                  ...facets.data.suppliers.map((supplier) => ({
                    value: supplier.value,
                    label: `${supplier.label} (${supplier.count})`,
                  })),
                ]}
                value={filters.supplier ?? "any"}
                onValueChange={(value) => update({ supplier: value === "any" ? null : value })}
              />
            ) : null}
          </Toolbar>

          {filters.material || filters.import_job ? (
            <div className={styles.chips}>
              {filters.material ? (
                <span className={styles.chip}>
                  Material: {materialName ?? "selected material"}
                  <IconButton
                    icon="x"
                    size="sm"
                    label="Show every material"
                    onClick={() => update({ material: null })}
                  />
                </span>
              ) : null}
              {filters.import_job ? (
                <span className={styles.chip}>
                  From one import
                  <IconButton
                    icon="x"
                    size="sm"
                    label="Show lots from every source"
                    onClick={() => update({ import: null })}
                  />
                </span>
              ) : null}
            </div>
          ) : null}

          <DataTable
            caption="Lots"
            columns={columns}
            rows={lots.data?.items}
            getRowId={(lot) => lot.id}
            loading={lots.isPending}
            error={lots.error}
            onRetry={() => void lots.refetch()}
            sort={sort}
            onSortChange={(next) =>
              update({
                sort: next ? `${next.direction === "desc" ? "-" : ""}${SORT_KEYS[next.columnId] ?? "material"}` : null,
              })
            }
            rowClassName={inactiveRowClass}
            rowActions={lotActions}
            rowActionsLabel={(lot) => `Actions for lot ${lot.display_code}`}
            stickyHeader
            empty={
              filtersActive ? (
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  title="No lots match these filters"
                  actions={<Button onClick={clearFilters}>Clear filters</Button>}
                />
              ) : facets.data?.on_order ? (
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  icon="truck"
                  title="Nothing on the shelf yet"
                  description={`${pluralize(facets.data.on_order, "lot is", "lots are")} on order. Receive them as they arrive and they show up here.`}
                  actions={
                    <ButtonLink to="/inventory?tab=orders" variant="primary">
                      See what’s on order
                    </ButtonLink>
                  }
                />
              ) : (
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  icon="box"
                  title="No lots yet"
                  description="Every container you own becomes a lot: receive one by hand, or paste a supplier order to add them all at once."
                />
              )
            }
          />
          {total > PAGE_SIZE ? (
            <nav className={styles.pager} aria-label="Pages of lots">
              <span className={styles.secondary}>
                {firstShown}–{lastShown} of {total}
              </span>
              <Button
                size="sm"
                icon="chevronLeft"
                disabled={page === 0}
                onClick={() => update({ page: String(page - 1) }, { keepPage: true })}
              >
                Previous
              </Button>
              <Button
                size="sm"
                iconEnd="chevronRight"
                disabled={lastShown >= total}
                onClick={() => update({ page: String(page + 1) }, { keepPage: true })}
              >
                Next
              </Button>
            </nav>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value="orders" className={styles.panel}>
          <section className={styles.section} aria-labelledby="orders-waiting">
            <h2 id="orders-waiting" className={styles.sectionTitle}>
              Waiting to be received
            </h2>
            <OrdersTable
              caption="Orders with containers still on order"
              orders={orders.data?.items.filter((order) => order.on_order_count > 0)}
              loading={orders.isPending}
              error={orders.error}
              onRetry={() => void orders.refetch()}
              empty={
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  icon="truck"
                  title="Nothing is on order"
                  description="Paste an order confirmation when you place an order: its containers wait here until you receive them."
                />
              }
            />
          </section>
          {orders.data?.items.some((order) => order.on_order_count === 0) ? (
            <section className={styles.section} aria-labelledby="orders-received">
              <h2 id="orders-received" className={styles.sectionTitle}>
                Received
              </h2>
              <OrdersTable
                caption="Orders received in full"
                orders={orders.data.items.filter((order) => order.on_order_count === 0)}
              />
            </section>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value="low" className={styles.panel}>
          <section className={styles.section} aria-labelledby="below-reorder">
            <h2 id="below-reorder" className={styles.sectionTitle}>
              Below their reorder point
            </h2>
            <DataTable
              caption="Materials below their reorder point"
              columns={lowMaterialColumns}
              rows={lowStock.data?.materials}
              getRowId={(item) => item.material.id}
              loading={lowStock.isPending}
              error={lowStock.error}
              onRetry={() => void lowStock.refetch()}
              rowActions={
                canEdit
                  ? () => [{ label: "Change reorder point…", icon: "pencil", onSelect: (item) => setReorderFor(item) }]
                  : undefined
              }
              rowActionsLabel={(item) => `Actions for ${item.material.name}`}
              empty={
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  title="Nothing is below its reorder point"
                  description="Set a reorder point on a lot's page for materials you never want to run out of."
                />
              }
            />
          </section>
          <section className={styles.section} aria-labelledby="running-low">
            <h2 id="running-low" className={styles.sectionTitle}>
              Containers running low
            </h2>
            <p className={styles.sectionHint}>
              Lots with less than{" "}
              {lowStock.data ? formatFractionAsPercent(lowStock.data.fraction, { decimals: 0 }) : "a fifth"} of what
              they held when full.
            </p>
            <DataTable
              caption="Lots running low"
              columns={columns.filter((column) => column.id !== "opened")}
              rows={lowStock.data?.lots}
              getRowId={(lot) => lot.id}
              loading={lowStock.isPending}
              rowActions={lotActions}
              rowActionsLabel={(lot) => `Actions for lot ${lot.display_code}`}
              empty={<EmptyState variant="inline" headingLevel={3} title="No container is running low" />}
            />
          </section>
        </Tabs.Panel>
      </Tabs.Root>

      {action && action.kind === "move" ? (
        <MoveLotDialog lot={action.lot} open onOpenChange={(open) => !open && setAction(null)} />
      ) : null}
      {action && action.kind === "adjust" ? (
        <AdjustLotDialog lot={action.lot} open onOpenChange={(open) => !open && setAction(null)} />
      ) : null}
      {action && action.kind === "dispose" ? (
        <DisposeLotDialog lot={action.lot} open onOpenChange={(open) => !open && setAction(null)} />
      ) : null}
      {action && action.kind === "receive" ? (
        <ReceiveLotDialog lot={action.lot} open onOpenChange={(open) => !open && setAction(null)} />
      ) : null}
      {action && action.kind === "remove" ? (
        <RemoveFromOrderDialog lot={action.lot} open onOpenChange={(open) => !open && setAction(null)} />
      ) : null}
      {action && action.kind === "label" ? (
        <PrintLabelsDialog
          open
          onOpenChange={(open) => !open && setAction(null)}
          itemKind="lot"
          itemIds={[action.lot.id]}
          subject={`lot ${action.lot.display_code}`}
        />
      ) : null}
      {reorderFor ? (
        <ReorderPointDialog
          material={reorderFor.material}
          currentMin={reorderFor.min_g}
          open
          onOpenChange={(open) => !open && setReorderFor(null)}
        />
      ) : null}
    </div>
  );
}
